import { APP_PREFIX_PATH } from 'configs/AppConfig';
import { UserType } from 'schemas/user.schemas';

/** Post-login landing path for Ads Manager. */
export const ADS_MANAGER_DEFAULT_PATH = `${APP_PREFIX_PATH}/architect-images/list`;

/** Post-login landing path for superadmin and Admin Viewer. */
export const SUPERADMIN_DEFAULT_PATH = `${APP_PREFIX_PATH}/dashboards/default`;

export const ADMIN_ACCESS_REQUIRED_MESSAGE = 'Admin access required.';

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function isAdminPanelUser(user) {
  if (user == null || user.type == null) {
    return false;
  }
  const type = Number(user.type);
  return (
    type === UserType.SUPERADMIN ||
    type === UserType.ADS_MANAGER ||
    type === UserType.ADMIN_VIEWER
  );
}

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function isAdsManager(user) {
  if (user == null || user.type == null) {
    return false;
  }
  return Number(user.type) === UserType.ADS_MANAGER;
}

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function isAdminViewer(user) {
  if (user == null || user.type == null) {
    return false;
  }
  return Number(user.type) === UserType.ADMIN_VIEWER;
}

/**
 * Superadmin-only mutations in Splitshire Admin.
 *
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function canWriteAdmin(user) {
  if (user == null || user.type == null) {
    return false;
  }
  return Number(user.type) === UserType.SUPERADMIN;
}

/**
 * Read-only admin operator (Ads Manager or Admin Viewer).
 *
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function isReadOnlyAdmin(user) {
  return isAdminPanelUser(user) && !canWriteAdmin(user);
}

const ADS_MANAGER_LOGIN_AS_TARGET_TYPES = new Set([
  UserType.APP_USER,
  UserType.PROMPT_MANAGER,
]);

/**
 * Whether the signed-in admin may show Login as for `target`.
 * Superadmin: any target. Ads Manager: App User and Prompt Manager only.
 * Admin Viewer: never.
 *
 * @param {{ type?: number | string } | null | undefined} currentUser
 * @param {{ type?: number | string } | null | undefined} target
 * @returns {boolean}
 */
export function canShowLoginAsUser(currentUser, target) {
  if (currentUser == null || target == null || target.type == null) {
    return false;
  }
  if (isAdminViewer(currentUser)) {
    return false;
  }
  if (isAdsManager(currentUser)) {
    return ADS_MANAGER_LOGIN_AS_TARGET_TYPES.has(Number(target.type));
  }
  return isAdminPanelUser(currentUser);
}

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {string}
 */
export function getAuthenticatedEntryPath(user) {
  return isAdsManager(user) ? ADS_MANAGER_DEFAULT_PATH : SUPERADMIN_DEFAULT_PATH;
}

const ADS_MANAGER_ALLOWED_PATHS = [
  `${APP_PREFIX_PATH}/architect-images/list`,
  `${APP_PREFIX_PATH}/users/list`,
  `${APP_PREFIX_PATH}/users/view`,
  `${APP_PREFIX_PATH}/pages/setting/change-password`,
];

/**
 * Write / create-edit routes blocked for Admin Viewer (soft redirect).
 */
const ADMIN_VIEWER_BLOCKED_PATH_PATTERNS = [
  /\/create(?:\/|$)/,
  /\/edit(?:\/|$)/,
  /\/pages\/setting\/edit-profile(?:\/|$)/,
];

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isPathAllowedForAdsManager(pathname) {
  const normalized = (pathname || '').replace(/\/$/, '') || '/';
  return ADS_MANAGER_ALLOWED_PATHS.some(
    (allowed) => normalized === allowed || normalized.startsWith(`${allowed}/`),
  );
}

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isWritePathBlockedForAdminViewer(pathname) {
  const normalized = (pathname || '').replace(/\/$/, '') || '/';
  return ADMIN_VIEWER_BLOCKED_PATH_PATTERNS.some((pattern) =>
    pattern.test(normalized),
  );
}

/**
 * List/detail fallback when Admin Viewer hits a blocked write path.
 *
 * @param {string} pathname
 * @returns {string}
 */
export function getAdminViewerWriteRedirectPath(pathname) {
  const normalized = (pathname || '').replace(/\/$/, '') || '/';
  if (normalized.includes('/pages/setting/')) {
    return `${APP_PREFIX_PATH}/pages/setting/change-password`;
  }
  const createEditMatch = normalized.match(
    /^(.*?)\/(?:create|edit)(?:\/[^/]*)?$/,
  );
  if (createEditMatch && createEditMatch[1]) {
    const base = createEditMatch[1];
    if (base.endsWith('/users')) {
      return `${base}/list`;
    }
    if (base.includes('/list')) {
      return base;
    }
    return `${base}/list`;
  }
  return SUPERADMIN_DEFAULT_PATH;
}

/**
 * @param {Array<Record<string, unknown>>} navConfig
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {Array<Record<string, unknown>>}
 */
export function filterNavigationForUser(navConfig, user) {
  if (!isAdsManager(user)) {
    return navConfig;
  }

  return navConfig
    .filter((item) => item.key === 'media')
    .map((item) => ({
      ...item,
      path: ADS_MANAGER_DEFAULT_PATH,
      submenu: (item.submenu || []).filter(
        (sub) => sub.key === 'architect-images.list',
      ),
    }));
}
